import type {
  AnalysisReport,
  ExtractionResult,
  FileMetadata,
  ProcessingLimits,
  ProgressUpdate,
  RepairResult,
  StructuredError,
  WorkerRequest,
  WorkerResponse,
} from '../engine/core/types';

export class WorkerOperationError extends Error {
  readonly details: StructuredError;

  constructor(details: StructuredError) {
    super(details.message);
    this.name = 'WorkerOperationError';
    this.details = details;
  }
}

type OperationResult = AnalysisReport | RepairResult | ExtractionResult;

export class WorkerManager {
  private activeWorker: Worker | null = null;
  private activeReject: ((reason: WorkerOperationError) => void) | null = null;

  async analyze(file: File, limits: ProcessingLimits, onProgress: (progress: ProgressUpdate) => void): Promise<AnalysisReport> {
    return this.run('analyze', file, limits, onProgress) as Promise<AnalysisReport>;
  }

  async repair(file: File, limits: ProcessingLimits, onProgress: (progress: ProgressUpdate) => void): Promise<RepairResult> {
    return this.run('repair', file, limits, onProgress) as Promise<RepairResult>;
  }

  async extract(file: File, limits: ProcessingLimits, onProgress: (progress: ProgressUpdate) => void): Promise<ExtractionResult> {
    return this.run('extract', file, limits, onProgress) as Promise<ExtractionResult>;
  }

  cancel(): void {
    this.activeWorker?.terminate();
    this.activeWorker = null;
    this.activeReject?.(new WorkerOperationError({
      code: 'PROCESSING_CANCELLED',
      title: 'Processing cancelled',
      message: 'The local processing worker was stopped.',
      technical: 'Cancelled by user or superseded by a new operation.',
      recommendation: 'Choose the file again when you are ready.',
      recoverable: true,
    }));
    this.activeReject = null;
  }

  private async run(
    operation: WorkerRequest['operation'],
    file: File,
    limits: ProcessingLimits,
    onProgress: (progress: ProgressUpdate) => void,
  ): Promise<OperationResult> {
    this.cancel();
    const buffer = await file.arrayBuffer();
    const metadata: FileMetadata = { name: file.name, size: file.size, type: file.type, lastModified: file.lastModified };
    const id = crypto.randomUUID();
    const worker = new Worker(new URL('./processing.worker.ts', import.meta.url), { type: 'module', name: `mendbyte-${operation}` });
    this.activeWorker = worker;
    return new Promise<OperationResult>((resolve, reject) => {
      this.activeReject = reject;
      const cleanup = (): void => {
        worker.terminate();
        if (this.activeWorker === worker) this.activeWorker = null;
        this.activeReject = null;
      };
      worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
        const response = event.data;
        if (response.id !== id) return;
        if (response.type === 'progress') {
          onProgress(response.progress);
          return;
        }
        cleanup();
        if (response.type === 'error') {
          reject(new WorkerOperationError(response.error));
        } else if (response.type === 'analysis') {
          resolve(response.report);
        } else if (response.type === 'repair') {
          resolve(response.result);
        } else {
          resolve(response.result);
        }
      });
      worker.addEventListener('error', (event) => {
        cleanup();
        reject(new WorkerOperationError({
          code: 'WORKER_FAILURE',
          title: 'The local processing worker stopped unexpectedly',
          message: 'MendByte could not finish this operation.',
          technical: event.message,
          recommendation: 'Try again with other memory-intensive tabs closed.',
          recoverable: true,
        }));
      });
      const request: WorkerRequest = { id, operation, payload: { metadata, buffer, limits } };
      worker.postMessage(request, [buffer]);
    });
  }
}
